/**
 * Vista 8 · Cola de Mesa de Partes.
 *
 * `ARQ-11`: superficie propia. **Sin estado de verificación ni los demás
 * documentos del trabajador.** Mesa de Partes necesita saber qué oficio mover
 * y adónde; no necesita saber que a ese trabajador se le está fiscalizando el
 * título, ni cuántos de sus documentos salieron irregulares. La proyección del
 * servidor ya viene recortada, así que esta vista no puede filtrar de más ni
 * de menos: no tiene los datos.
 *
 * Las cuatro secciones son **dato**, con su paso destino declarado, y cada una
 * ofrece EXACTAMENTE la acción del paso que le toca — nunca un rango abierto.
 */
import { useState } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mpfn/ui'
import { DataTable, EmptyState, PageHeader, type DataTableColumn } from '@mpfn/patterns'
import { Inbox } from 'lucide-react'
import {
  useAsignarNotificador,
  useColaMesaPartes,
  useHistorialMesaPartes,
  useNotificadores,
} from '../api/consultas.js'
import type { ColaMesaPartes as Cola, OficioEnCircuito, PasoHistorial } from '../api/tipos.js'
import { Datos } from '../componentes/Datos.js'
import { Pagina } from '../componentes/Pagina.js'
import { DialogoPaso } from '../componentes/DialogoPaso.js'
import { avisarError } from '../lib/avisos.js'
import { fecha } from '../lib/formato.js'

/** Las cuatro secciones, con el paso que cada una registra. */
const SECCIONES = [
  { clave: 'porRecibir', titulo: 'Oficios por recibir', paso: 2, accion: 'Recibir' },
  { clave: 'porDespachar', titulo: 'Oficios por despachar', paso: 3, accion: 'Despachar' },
  { clave: 'cargosPorRecibir', titulo: 'Cargos por recibir', paso: 7, accion: 'Recibir el cargo' },
  { clave: 'cargosPorInformar', titulo: 'Cargos por informar', paso: 8, accion: 'Informar' },
] as const satisfies ReadonlyArray<{
  clave: keyof Cola
  titulo: string
  paso: number
  accion: string
}>

type Pestana = 'pendientes' | 'historial'

export function ColaMesaPartes() {
  const cola = useColaMesaPartes()
  const historial = useHistorialMesaPartes()
  const [pestana, setPestana] = useState<Pestana>('pendientes')

  return (
    <Pagina>
      <PageHeader
        title="Cola de Mesa de Partes"
        description="Solo oficios del canal físico: los demás canales no pasan por aquí."
        action={
          // El DS no tiene `Tabs`; dos botones con `aria-pressed` cumplen el
          // mismo papel sin inventar un componente. `<fieldset>` en vez de
          // `role="group"`: el elemento nativo ya agrupa, y la leyenda oculta
          // le da el nombre accesible.
          <fieldset className="sigfisc-segmentado">
            <legend className="mpfn-sr-only">Vista de la cola</legend>
            <Button
              variant={pestana === 'pendientes' ? 'primary' : 'ghost'}
              size="sm"
              aria-pressed={pestana === 'pendientes'}
              onClick={() => {
                setPestana('pendientes')
              }}
            >
              Pendientes
            </Button>
            <Button
              variant={pestana === 'historial' ? 'primary' : 'ghost'}
              size="sm"
              aria-pressed={pestana === 'historial'}
              onClick={() => {
                setPestana('historial')
              }}
            >
              Historial
            </Button>
          </fieldset>
        }
      />

      {pestana === 'pendientes' ? (
        <Datos estado={cola}>
          {(datos) => {
            const vacia = SECCIONES.every((seccion) => datos[seccion.clave].length === 0)
            if (vacia) {
              return (
                <EmptyState
                  icon={Inbox}
                  title="No hay oficios pendientes"
                  description="Cuando Fiscalización envíe un oficio por el canal físico, aparecerá aquí."
                />
              )
            }
            return (
              <>
                {SECCIONES.map((seccion) => (
                  <SeccionDeCola
                    key={seccion.clave}
                    titulo={seccion.titulo}
                    paso={seccion.paso}
                    etiquetaAccion={seccion.accion}
                    exigeNotificador={seccion.clave === 'porDespachar'}
                    oficios={datos[seccion.clave]}
                  />
                ))}
              </>
            )
          }}
        </Datos>
      ) : (
        <Datos estado={historial}>{(pasos) => <Historial pasos={pasos} />}</Datos>
      )}
    </Pagina>
  )
}

function SeccionDeCola({
  titulo,
  paso,
  etiquetaAccion,
  exigeNotificador,
  oficios,
}: {
  titulo: string
  paso: number
  etiquetaAccion: string
  exigeNotificador: boolean
  oficios: readonly OficioEnCircuito[]
}) {
  const notificadores = useNotificadores()
  const asignar = useAsignarNotificador()
  const [enProceso, setEnProceso] = useState<OficioEnCircuito | undefined>(undefined)
  const [asignaciones, setAsignaciones] = useState<Readonly<Record<string, string>>>({})

  const columnas: ReadonlyArray<DataTableColumn<OficioEnCircuito>> = [
    { id: 'oficio', header: 'Oficio', cell: (fila) => fila.numeroOficio },
    { id: 'entidad', header: 'Entidad', cell: (fila) => fila.entidad },
    { id: 'destino', header: 'Destino', cell: (fila) => fila.destino, hideOnMobile: true },
    { id: 'emitido', header: 'Emitido', cell: (fila) => fecha(fila.fechaApertura), hideOnMobile: true },
    {
      id: 'accion',
      header: '',
      align: 'right',
      cell: (fila) => (
        <span className="sigfisc-cola__acciones">
          {exigeNotificador && (
            <Select
              value={asignaciones[fila.consultaId] ?? fila.notificadorId ?? ''}
              onValueChange={(valor) => {
                setAsignaciones((previo) => ({ ...previo, [fila.consultaId]: valor }))
              }}
            >
              <SelectTrigger aria-label={`Notificador para el oficio ${fila.numeroOficio}`}>
                <SelectValue placeholder="Notificador" />
              </SelectTrigger>
              <SelectContent>
                {(notificadores.data ?? []).map((persona) => (
                  <SelectItem key={persona.id} value={persona.id}>
                    {persona.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="secondary"
            size="sm"
            loading={asignar.isPending && asignar.variables?.consultaId === fila.consultaId}
            disabled={exigeNotificador && (asignaciones[fila.consultaId] ?? fila.notificadorId) === undefined}
            onClick={() => {
              if (!exigeNotificador) {
                setEnProceso(fila)
                return
              }
              // Despachar son dos hechos: a quién se le entrega y que se
              // entregó. Se encadenan para que no quede uno sin el otro.
              const notificadorId = asignaciones[fila.consultaId] ?? fila.notificadorId
              if (!notificadorId) return
              asignar.mutate(
                { consultaId: fila.consultaId, notificadorId },
                {
                  onSuccess: () => {
                    setEnProceso(fila)
                  },
                  onError: (error) => {
                    avisarError(error, 'No se pudo asignar el notificador')
                  },
                },
              )
            }}
          >
            {etiquetaAccion}
          </Button>
        </span>
      ),
    },
  ]

  return (
    <Card>
      <CardHeader className="sigfisc-seccion-card__cabecera">
        <CardTitle>
          {titulo} · {oficios.length}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columnas}
          rows={oficios}
          rowKey={(fila) => fila.consultaId}
          empty={{ title: 'Nada pendiente en esta sección' }}
        />
      </CardContent>

      {enProceso && (
        <DialogoPaso
          consultaId={enProceso.consultaId}
          canal="fisico"
          paso={paso}
          abierto
          onCambio={(abierto) => {
            if (!abierto) setEnProceso(undefined)
          }}
        />
      )}
    </Card>
  )
}

function Historial({ pasos }: { pasos: readonly PasoHistorial[] }) {
  const columnas: ReadonlyArray<DataTableColumn<PasoHistorial>> = [
    { id: 'fecha', header: 'Fecha', cell: (fila) => fecha(fila.fecha) },
    { id: 'oficio', header: 'Oficio', cell: (fila) => fila.numero_oficio },
    { id: 'entidad', header: 'Entidad', cell: (fila) => fila.entidad, hideOnMobile: true },
    { id: 'paso', header: 'Paso', cell: (fila) => fila.paso, align: 'right' },
    {
      id: 'observacion',
      header: 'Observación',
      cell: (fila) => fila.observacion ?? '—',
      hideOnMobile: true,
    },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle>Últimos movimientos registrados</CardTitle>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columnas}
          rows={pasos}
          rowKey={(fila) => `${fila.consulta_id}-${String(fila.paso)}`}
          empty={{ title: 'Todavía no hay movimientos registrados' }}
        />
      </CardContent>
    </Card>
  )
}
